import axios, { AxiosResponse } from 'axios';
import { LoginData, RegisterData, TokenResponse, User } from '../types/auth';

const API_BASE_URL = process.env.REACT_APP_API_URL || (window.location.protocol === 'https:' ? 'https://sslmo-dambi.com' : 'http://localhost:8002');

// Axios 인스턴스 생성
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: false, // CORS * origin과 호환되도록 false 설정
  timeout: 10000, // 10초 타임아웃
});

// 토큰 저장소
class TokenService {
  private static ACCESS_TOKEN_KEY = 'access_token';
  private static REFRESH_TOKEN_KEY = 'refresh_token';

  static getAccessToken(): string | null {
    return localStorage.getItem(this.ACCESS_TOKEN_KEY);
  }

  static getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  static setTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem(this.ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
  }

  static clearTokens(): void {
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
  }
}

// 요청 인터셉터 - 토큰 자동 추가
api.interceptors.request.use(
  (config: any) => {
    console.log('🚀 API 요청:', config.method?.toUpperCase(), config.url);
    console.log('📡 Base URL:', config.baseURL);
    
    const token = TokenService.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: any) => {
    console.error('❌ 요청 오류:', error);
    return Promise.reject(error);
  }
);

// 응답 인터셉터 - 토큰 갱신 처리
api.interceptors.response.use(
  (response: any) => {
    console.log('✅ API 응답 성공:', response.status, response.config.url);
    return response;
  },
  async (error: any) => {
    console.error('❌ API 응답 오류:', error.response?.status, error.message);
    console.error('🔍 오류 상세:', error.response?.data);
    
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = TokenService.getRefreshToken();
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refresh_token: refreshToken,
          });

          const { access_token } = response.data;
          TokenService.setTokens(access_token, refreshToken);

          // 실패한 요청 재시도
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // 리프레시 토큰도 만료된 경우
        TokenService.clearTokens();
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// Auth API functions
export const authAPI = {
  // Login
  login: async (credentials: { email: string; password: string }) => {
    const response = await api.post('/api/sslmo/auth/login', credentials);
    return response.data;
  },

  // Register
  register: async (userData: {
    username: string;
    email: string;
    password: string;
    full_name?: string;
    phone?: string;
  }) => {
    const response = await api.post('/api/sslmo/auth/register', userData);
    return response.data;
  },

  // Get current user info
  getCurrentUser: async () => {
    const response = await api.get('/api/sslmo/auth/me');
    return response.data;
  },

  // Get dashboard stats
  getDashboardStats: async () => {
    const response = await api.get('/api/sslmo/auth/me/dashboard-stats');
    return response.data;
  },

  // Get my collection requests (디버깅용)
  getMyCollectionRequests: async () => {
    const response = await api.get('/api/sslmo/auth/me/collection-requests');
    return response.data;
  },

  // Change password
  changePassword: async (passwordData: {
    current_password: string;
    new_password: string;
  }) => {
    const response = await api.post('/api/sslmo/auth/change-password', passwordData);
    return response.data;
  },

  // Request password reset
  requestPasswordReset: async (email: string) => {
    const response = await api.post('/api/sslmo/auth/forgot-password', { email });
    return response.data;
  },

  // Reset password
  resetPassword: async (resetData: {
    token: string;
    new_password: string;
  }) => {
    const response = await api.post('/api/sslmo/auth/reset-password', resetData);
    return response.data;
  },

  // Update profile
  updateProfile: async (profileData: {
    full_name?: string;
    email?: string;
    phone?: string;
  }) => {
    const response = await api.put('/api/sslmo/auth/me/profile', profileData);
    return response.data;
  },

  // Refresh token
  refreshToken: async (refreshToken: string) => {
    const response = await api.post('/api/sslmo/auth/refresh', { refresh_token: refreshToken });
    return response.data;
  },

  // Logout
  logout: async () => {
    const response = await api.post('/api/sslmo/auth/logout');
    return response.data;
  },

  // Recurring Collections
  getMyRecurringSchedules: async () => {
    const response = await api.get('/api/sslmo/auth/recurring-collections');
    return response.data;
  },

  createRecurringSchedule: async (scheduleData: any) => {
    const response = await api.post('/api/sslmo/auth/recurring-collections', scheduleData);
    return response.data;
  },

  updateRecurringSchedule: async (scheduleId: number, scheduleData: any) => {
    const response = await api.put(`/api/sslmo/auth/recurring-collections/${scheduleId}`, scheduleData);
    return response.data;
  },

  toggleRecurringSchedule: async (scheduleId: number) => {
    const response = await api.patch(`/api/sslmo/auth/recurring-collections/${scheduleId}/toggle`);
    return response.data;
  },

  deleteRecurringSchedule: async (scheduleId: number) => {
    const response = await api.delete(`/api/sslmo/auth/recurring-collections/${scheduleId}`);
    return response.data;
  },
};

// Admin API functions
export const adminAPI = {
  // Get system statistics
  getSystemStats: async () => {
    const response = await api.get('/api/sslmo/auth/admin/stats');
    return response.data;
  },

  // Get all users with pagination and filtering
  getUsers: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    role?: 'all' | 'user' | 'admin' | 'collector';
    status?: 'all' | 'active' | 'inactive';
  }) => {
    const response = await api.get('/api/sslmo/auth/admin/users', { params });
    return response.data;
  },

  // Update user role
  updateUserRole: async (userId: number, role: 'user' | 'admin' | 'collector') => {
    const response = await api.patch(`/api/sslmo/auth/admin/users/${userId}/role`, { role });
    return response.data;
  },

  // Toggle user active status
  toggleUserStatus: async (userId: number) => {
    const response = await api.patch(`/api/sslmo/auth/admin/users/${userId}/toggle-status`);
    return response.data;
  },

  // Delete user
  deleteUser: async (userId: number) => {
    const response = await api.delete(`/api/sslmo/auth/admin/users/${userId}`);
    return response.data;
  },

  // Get activity logs
  getActivityLogs: async (params?: {
    page?: number;
    limit?: number;
    type?: 'all' | 'user' | 'system' | 'security';
    from_date?: string;
    to_date?: string;
  }) => {
    const response = await api.get('/api/sslmo/auth/admin/activity-logs', { params });
    return response.data;
  },

  // Get collections management data
  getCollections: async (params?: {
    page?: number;
    limit?: number;
    status?: 'all' | 'active' | 'inactive' | 'processing';
    search?: string;
  }) => {
    const response = await api.get('/api/sslmo/auth/admin/collections', { params });
    return response.data;
  },

  // Update collection status
  updateCollectionStatus: async (collectionId: number, status: string) => {
    const response = await api.patch(`/api/sslmo/auth/admin/collections/${collectionId}/status`, { status });
    return response.data;
  },

  // Delete collection
  deleteCollection: async (collectionId: number) => {
    const response = await api.delete(`/api/sslmo/auth/admin/collections/${collectionId}`);
    return response.data;
  },

  // Get analytics data
  getAnalytics: async (params?: {
    period?: 'day' | 'week' | 'month' | 'year';
    from_date?: string;
    to_date?: string;
  }) => {
    const response = await api.get('/api/sslmo/auth/admin/analytics', { params });
    return response.data;
  },

  // Get AI analysis performance metrics
  getAIMetrics: async (params?: {
    period?: 'day' | 'week' | 'month';
  }) => {
    const response = await api.get('/api/sslmo/auth/admin/ai-metrics', { params });
    return response.data;
  },

  // Get billing configuration
  getBillingConfig: async () => {
    const response = await api.get('/api/sslmo/auth/admin/billing/config');
    return response.data;
  },

  // Update billing configuration
  updateBillingConfig: async (config: any) => {
    const response = await api.put('/api/sslmo/auth/admin/billing/config', config);
    return response.data;
  },

  // Get billing usage data
  getBillingUsage: async (params?: {
    user_id?: number;
    from_date?: string;
    to_date?: string;
  }) => {
    const response = await api.get('/api/sslmo/auth/admin/billing/usage', { params });
    return response.data;
  },

  // Export data
  exportData: async (type: 'users' | 'collections' | 'logs' | 'analytics', format: 'csv' | 'json' | 'excel') => {
    const response = await api.get(`/api/sslmo/admin/export/${type}`, {
      params: { format },
      responseType: 'blob'
    });
    return response.data;
  },

  // Get system health
  getSystemHealth: async () => {
    const response = await api.get('/api/sslmo/admin/system/health');
    return response.data;
  },

  // Get system notifications
  getSystemNotifications: async () => {
    const response = await api.get('/api/sslmo/admin/notifications');
    return response.data;
  },

  // Create system notification
  createSystemNotification: async (notification: {
    title: string;
    message: string;
    type: 'info' | 'warning' | 'error' | 'success';
    target_users?: number[];
  }) => {
    const response = await api.post('/api/sslmo/admin/notifications', notification);
    return response.data;
  },

  // Update system notification
  updateSystemNotification: async (notificationId: number, notification: any) => {
    const response = await api.put(`/api/sslmo/admin/notifications/${notificationId}`, notification);
    return response.data;
  },

  // Delete system notification
  deleteSystemNotification: async (notificationId: number) => {
    const response = await api.delete(`/api/sslmo/admin/notifications/${notificationId}`);
    return response.data;
  }
};

// Collection Request API functions
export const collectionRequestAPI = {
  // Create new collection request
  createRequest: async (requestData: {
    waste_type?: string;
    description: string;
    estimated_weight?: number;
    estimated_volume?: number;
    collection_address: string;
    collection_date?: string;
    collection_time_preference?: string;
    urgency?: string;
    special_instructions?: string;
    contact_phone?: string;
    is_recurring?: boolean;
  }) => {
    const response = await api.post('/api/sslmo/auth/collection-requests/', requestData);
    return response.data;
  },

  // Get collection requests with pagination and filters
      getRequests: async (params?: {
      page?: number;
      size?: number;
      waste_type?: string;
      status?: string;
      urgency?: string;
    }) => {
      const response = await api.get('/api/sslmo/auth/collection-requests/', { params });
      return response.data;
    },

  // Get specific collection request
  getRequest: async (requestId: number) => {
    const response = await api.get(`/api/sslmo/auth/collection-requests/${requestId}`);
    return response.data;
  },

  // Update collection request
  updateRequest: async (requestId: number, updateData: any) => {
    const response = await api.put(`/api/sslmo/auth/collection-requests/${requestId}`, updateData);
    return response.data;
  },

  // Update request status (admin only)
  updateRequestStatus: async (requestId: number, statusData: {
    status: string;
    assigned_collector_id?: number;
    completion_notes?: string;
    actual_weight?: number;
    cancellation_reason?: string;
  }) => {
    const response = await api.patch(`/api/sslmo/auth/collection-requests/${requestId}/status`, statusData);
    return response.data;
  },

  // Cancel collection request
  cancelRequest: async (requestId: number, cancellation_reason?: string) => {
    const response = await api.delete(`/api/sslmo/auth/collection-requests/${requestId}`, {
      params: { cancellation_reason }
    });
    return response.data;
  },

  // Get pending requests (admin only)
  getPendingRequests: async (limit?: number) => {
    const response = await api.get('/api/sslmo/auth/collection-requests/admin/pending', {
      params: { limit }
    });
    return response.data;
  },

  // Get receipt for completed collection request
  getReceipt: async (requestId: number) => {
    const response = await api.get(`/api/sslmo/auth/collection-requests/${requestId}/receipt`);
    return response.data;
  },

  // Get collection analysis data
  getAnalysis: async (period: string = '6months', chartType: string = 'monthly') => {
    const response = await api.get('/api/sslmo/auth/collection-requests/analysis', {
      params: { period, chart_type: chartType }
    });
    return response.data;
  },

  // Update billing info for all completed requests (admin only)
  updateBillingInfo: async () => {
    const response = await api.post('/collection-requests/update-billing-info');
    return response.data;
  },
};

// Collector API functions
export const collectorAPI = {
  // Create new collector (admin only)
  createCollector: async (collectorData: {
    employee_id: string;
    phone: string;
    vehicle_number?: string;
    vehicle_type?: string;
    license_number?: string;
    work_area?: string;
    max_capacity?: number;
    work_start_time?: string;
    work_end_time?: string;
    emergency_contact?: string;
    notes?: string;
  }) => {
    const response = await api.post('/api/sslmo/auth/collectors', collectorData);
    return response.data;
  },

  // Get all collectors (admin only)
  getCollectors: async (params?: {
    skip?: number;
    limit?: number;
    status?: string;
    is_available?: boolean;
  }) => {
    const response = await api.get('/api/sslmo/auth/collectors', { params });
    return response.data;
  },

  // Get available collectors (admin only)
  getAvailableCollectors: async (work_area?: string) => {
    const response = await api.get('/api/sslmo/auth/collectors/available', {
      params: { work_area }
    });
    return response.data;
  },

  // Get my collector profile
  getMyProfile: async () => {
    const response = await api.get('/api/sslmo/auth/collectors/me');
    return response.data;
  },

  // Get my dashboard
  getMyDashboard: async () => {
    const response = await api.get('/api/sslmo/auth/collectors/me/dashboard');
    return response.data;
  },

  // Get specific collector
  getCollector: async (collectorId: number) => {
    const response = await api.get(`/api/sslmo/auth/collectors/${collectorId}`);
    return response.data;
  },

  // Update my profile
  updateMyProfile: async (updateData: {
    phone?: string;
    vehicle_number?: string;
    vehicle_type?: string;
    license_number?: string;
    work_area?: string;
    max_capacity?: number;
    work_start_time?: string;
    work_end_time?: string;
    emergency_contact?: string;
    notes?: string;
  }) => {
    const response = await api.put('/api/sslmo/auth/collectors/me', updateData);
    return response.data;
  },

  // Update my status
  updateMyStatus: async (statusData: {
    status: string;
    current_latitude?: number;
    current_longitude?: number;
    current_load?: number;
  }) => {
    const response = await api.patch('/api/sslmo/auth/collectors/me/status', statusData);
    return response.data;
  },

  // Update my location
  updateMyLocation: async (locationData: {
    latitude: number;
    longitude: number;
  }) => {
    const response = await api.patch('/api/sslmo/auth/collectors/me/location', locationData);
    return response.data;
  },

  // Get my assigned requests
  getMyRequests: async (status?: string) => {
    const response = await api.get('/api/sslmo/auth/collectors/me/requests', {
      params: { status }
    });
    return response.data;
  },

  // Start collection request
  startRequest: async (requestId: number) => {
    const response = await api.post(`/api/sslmo/auth/collectors/me/requests/${requestId}/start`);
    return response.data;
  },

  // Complete collection request
  completeRequest: async (requestId: number, data?: {
    actual_weight?: number;
    completion_notes?: string;
  }) => {
    const response = await api.post(`/api/sslmo/auth/collectors/me/requests/${requestId}/complete`, null, {
      params: data
    });
    return response.data;
  },

  // Update volume for completed request
  updateVolume: async (requestId: number, actualVolume: number) => {
    const formData = new FormData();
    formData.append('actual_volume', actualVolume.toString());
    
    const response = await api.patch(`/api/sslmo/auth/collectors/me/requests/${requestId}/update-volume`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

export { TokenService };
export default api;